'use client';

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';
import { isRailRoute } from '@/lib/app-shell-routes';
import { useLocale } from '@/hooks/use-locale';
import { useSession } from '@/hooks/use-session';

interface AppShellContextValue {
  signedInShell: boolean;
  /** Effective state: the saved preference, or the rail-route default. */
  sidebarCollapsed: boolean;
  /** The current page opens collapsed regardless of the saved preference. */
  sidebarDefaultRail: boolean;
  toggleSidebar: () => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

export function AppShellProvider({
  children,
  initialSignedIn,
  initialSidebarCollapsed,
}: {
  children: ReactNode;
  initialSignedIn: boolean;
  initialSidebarCollapsed: boolean;
}) {
  const { status } = useSession();
  const { internalPathname } = useLocale();
  // The user's saved preference — the only state the cookie ever stores.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    initialSidebarCollapsed,
  );
  // Wide data pages (search, tickers) open on the rail to free table width.
  // A toggle there is a page-local override: it is never persisted, and it
  // resets on every navigation so each visit starts collapsed again.
  const sidebarDefaultRail = isRailRoute(internalPathname);
  const [railOverride, setRailOverride] = useState<boolean | null>(null);
  const [overridePath, setOverridePath] = useState(internalPathname);
  if (overridePath !== internalPathname) {
    setOverridePath(internalPathname);
    setRailOverride(null);
  }
  const collapsed = sidebarDefaultRail
    ? (railOverride ?? true)
    : sidebarCollapsed;
  const widthProbe = useRef<HTMLDivElement>(null);
  const signedInShell =
    status === 'authenticated' || (status === 'loading' && initialSignedIn);

  const toggleSidebar = useCallback(() => {
    if (sidebarDefaultRail) setRailOverride((override) => !(override ?? true));
    else setSidebarCollapsed((current) => !current);
  }, [sidebarDefaultRail]);

  useLayoutEffect(() => {
    document.cookie = `fd_sidebar=${sidebarCollapsed ? 'collapsed' : 'expanded'}; path=/; max-age=31536000; samesite=lax`;
  }, [sidebarCollapsed]);

  useLayoutEffect(() => {
    document.body.dataset.appShell = signedInShell ? 'signed-in' : 'guest';
    document.body.dataset.sidebarCollapsed = String(collapsed);
    // CSS decides the width, including the lg rail / xl choice. Observe it
    // instead of duplicating the responsive layout with viewport JS.
    const publishWidth = () => {
      document.body.style.setProperty(
        '--app-sidebar-width',
        `${widthProbe.current?.offsetWidth ?? 0}px`,
      );
    };
    publishWidth();
    const observer = new ResizeObserver(publishWidth);
    if (widthProbe.current) observer.observe(widthProbe.current);
    return () => observer.disconnect();
  }, [signedInShell, collapsed]);

  return (
    <AppShellContext.Provider
      value={{
        signedInShell,
        sidebarCollapsed: collapsed,
        sidebarDefaultRail,
        toggleSidebar,
      }}
    >
      {/* The root layout's pre-hydration CSS keys the xl width on the saved
          preference and cannot see the path; pin the rail here (SSR-included,
          equal specificity, later in the document) so rail pages don't paint
          a 256px gap before publishWidth takes over. */}
      {sidebarDefaultRail && collapsed && (
        <style>{`@media (min-width: 1280px) { body[data-app-shell="signed-in"][data-sidebar-collapsed] { --app-sidebar-width: 64px; } }`}</style>
      )}
      <div
        ref={widthProbe}
        aria-hidden
        inert
        className={cn(
          'pointer-events-none invisible fixed h-0',
          signedInShell ? 'hidden w-16 lg:block' : 'hidden w-0',
          signedInShell && !collapsed && 'xl:w-64',
        )}
      />
      {children}
    </AppShellContext.Provider>
  );
}

export function useAppShell() {
  const context = useContext(AppShellContext);
  if (!context)
    throw new Error('useAppShell must be used within AppShellProvider');
  return context;
}
